import React, { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import {
    FileText,
    Filter,
    ChartColumn,
    AlertTriangle,
    SearchX,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { accessLogsApi, resourcesApi } from '../services/api';
import { AccessLog, Resource } from '../types';
import { redactJson } from '@/lib/redact';
import { fetchUserFilterOptions, userFilterHelp, userFilterQuery } from '../lib/userFilterOptions';
import { User as IAMUser } from '../types/iam';
import {
    Button, Card, Chip, Drawer, EmptyState, ListPageSkeleton, LogSearchTable, Modal, MonoCell, OutcomeBadge,
    PageHeader, Select, Timestamp, TimeRangePicker, type LogSearchColumn,
} from '@/components/ds-zta';
import { useUrlState, useUrlTimeRange } from '@/lib/useUrlState';
import { DEFAULT_RANGE, resolveRange } from '@/lib/timeRange';
import { filterToWindow, windowMayContinue, windowPhrase } from '@/features/audit/timeWindow';
import { policyHref, policyIdOf, resourcePageHref } from '@/features/audit/accessLogLinks';

// Rows read per request. The log is read newest first and appended as the
// table scrolls (ds LogSearchTable), until the loaded rows reach back past
// the start of the time range.
const BATCH = 100;

// Filters, the time range and the open entry live in the URL (design eval
// F24), so Back, refresh and a pasted link all reopen the same view.
const URL_DEFAULTS = { user_id: '', resource_id: '', result: '', entry: '' };
type FilterKey = 'user_id' | 'resource_id' | 'result';
const EMPTY_FILTERS: Record<FilterKey, string> = { user_id: '', resource_id: '', result: '' };

const createdAt = (log: AccessLog) => log.created_at;

// A link inside a LogSearchTable row: the grid is one tab stop with a roving
// row, so a cell link stays out of the tab order (the drawer carries the same
// links for the keyboard) and a click on it doesn't also open the row.
const rowLinkProps = {
    tabIndex: -1,
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    className: 'truncate text-[var(--d2d-text-primary)] underline-offset-2 hover:underline',
};

const AccessLogs: React.FC = () => {
    const [url, setUrl] = useUrlState(URL_DEFAULTS);
    const [range, setRange] = useUrlTimeRange(DEFAULT_RANGE, ['entry']);
    const applied = useMemo(
        () => ({ user_id: url.user_id, resource_id: url.resource_id, result: url.result }),
        [url.user_id, url.resource_id, url.result],
    );
    const [filters, setFilters] = useState(applied);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    // Fetch users for dropdown
    const { data: users } = useQuery(userFilterQuery, fetchUserFilterOptions);

    // Resources for the filter picker and for naming and linking a row's
    // resource. GET /resources is the admin list, and this page is admin-only.
    const { data: resourcesData } = useQuery(
        'access-log-resources',
        () => resourcesApi.listAll(undefined, undefined, 500, 0),
        { staleTime: 5 * 60_000, refetchOnWindowFocus: false },
    );
    const resourcesList: Resource[] = useMemo(() => resourcesData?.data?.resources ?? [], [resourcesData]);
    const resourceMap = useMemo(() => new Map(resourcesList.map((r) => [r.id, r])), [resourcesList]);

    // GET /access-logs filters by user, resource and result, and pages by
    // limit/offset. It takes no time window, so the range is applied to the
    // loaded rows below and the page says how far back they reach.
    const {
        data, isLoading, error, refetch, isFetching, fetchNextPage, isFetchingNextPage, dataUpdatedAt,
    } = useInfiniteQuery(
        ['access-logs', applied],
        ({ pageParam = 0 }) => accessLogsApi.list(
            applied.user_id || undefined,
            applied.resource_id || undefined,
            applied.result || undefined,
            BATCH,
            pageParam,
        ),
        {
            keepPreviousData: true,
            getNextPageParam: (last, pages) => {
                const loaded = pages.reduce((n, p) => n + (p.data?.access_logs?.length ?? 0), 0);
                const total = last.data?.total ?? 0;
                return loaded < total && (last.data?.access_logs?.length ?? 0) > 0 ? loaded : undefined;
            },
        },
    );

    const loaded = useMemo<AccessLog[]>(
        () => (data?.pages ?? []).flatMap((p) => p.data?.access_logs ?? []),
        [data],
    );
    const total = data?.pages?.[data.pages.length - 1]?.data?.total ?? 0;

    // Resolved at query time (each fetch), so a relative range stays live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const bounds = useMemo(() => resolveRange(range), [range, dataUpdatedAt]);
    const accessLogs = useMemo(() => filterToWindow(loaded, createdAt, bounds), [loaded, bounds]);
    const hasMore = windowMayContinue(loaded, createdAt, total, bounds);
    const windowText = windowPhrase(range);

    // The open entry: from the loaded rows, or fetched on its own when the
    // link was opened cold (a refresh, a pasted URL).
    const openId = url.entry;
    const openFromList = openId ? loaded.find((l) => l.id === openId) : undefined;
    const { data: openFetched, isLoading: openLoading, isError: openError } = useQuery(
        ['access-log', openId],
        () => accessLogsApi.get(openId),
        { enabled: !!openId && !openFromList && !isLoading, retry: false },
    );
    const openLog: AccessLog | undefined = openFromList ?? openFetched?.data;

    // Create a user lookup map for efficient username retrieval
    const usersList: IAMUser[] = useMemo(() => (Array.isArray(users) ? users : users?.data || []), [users]);
    const userMap = useMemo(() => {
        const map = new Map<string, IAMUser>();
        usersList.forEach((user: IAMUser) => {
            map.set(user.id, user);
        });
        return map;
    }, [usersList]);

    const getUserDisplayName = (userId: string) => {
        const user = userMap.get(userId);
        return user?.name || user?.email || null;
    };

    const getResultBadge = (result: string) => {
        // An access decision chip (design eval D1): squared, with its glyph.
        return result === 'allow'
            ? <OutcomeBadge value="allow" decision>Allow</OutcomeBadge>
            : <OutcomeBadge value="deny" decision>Deny</OutcomeBadge>;
    };


    const applyFilters = () => {
        setUrl({ ...filters, entry: null });
        setFilterModalOpen(false);
    };

    const resetFilters = () => {
        setFilters(EMPTY_FILTERS);
        setUrl({ ...EMPTY_FILTERS, entry: null });
    };

    const removeFilter = (filterKey: FilterKey) => {
        setUrl({ [filterKey]: null, entry: null });
    };

    const getActiveFilters = () => {
        const active: { key: FilterKey; label: string; value: React.ReactNode }[] = [];
        if (applied.user_id) {
            active.push({ key: 'user_id', label: 'User', value: getUserDisplayName(applied.user_id) || applied.user_id });
        }
        if (applied.resource_id) {
            const res = resourceMap.get(applied.resource_id);
            active.push({ key: 'resource_id', label: 'Resource', value: res ? res.external_id : <MonoCell value={applied.resource_id} width="12ch" copyable={false} /> });
        }
        if (applied.result) {
            active.push({ key: 'result', label: 'Result', value: applied.result });
        }
        return active;
    };
    const activeFilters = getActiveFilters();

    // A row's resource: its name and the page it is managed on.
    const resourceOf = (log: AccessLog): { name: string; href?: string } => {
        const res = log.resource_id ? resourceMap.get(log.resource_id) : undefined;
        const name = String(res?.external_id || log.request_context?.resource || log.request_context?.resource_name || log.resource_id || '');
        if (!name) return { name: '' };
        const type = res?.type || log.request_context?.resource_type;
        return { name, href: res || type ? resourcePageHref(type, name) : undefined };
    };

    const columns: LogSearchColumn<AccessLog>[] = [
        { key: 'time', header: 'Time', width: '200px', cell: (log) => <Timestamp value={log.created_at} /> },
        { key: 'result', header: 'Result', width: '96px', cell: (log) => getResultBadge(log.result) },
        { key: 'action', header: 'Action', cell: (log) => <span className="truncate">{log.action}</span> },
        {
            key: 'user', header: 'User', width: '200px',
            cell: (log) => log.user_id ? (
                <Link to={`/users/${encodeURIComponent(log.user_id)}`} {...rowLinkProps}>
                    {getUserDisplayName(log.user_id) || <span className="font-mono">{log.user_id}</span>}
                </Link>
            ) : '—',
        },
        {
            key: 'resource', header: 'Resource', width: '200px',
            cell: (log) => {
                const { name, href } = resourceOf(log);
                if (!name) return '—';
                return href ? <Link to={href} {...rowLinkProps}>{name}</Link> : <span className="truncate font-mono">{name}</span>;
            },
        },
        { key: 'protocol', header: 'Protocol', width: '96px', cell: (log) => log.request_context?.protocol || '—' },
    ];

    const loadedNote = loaded.length === 0
        ? null
        : hasMore
            ? `${accessLogs.length.toLocaleString()} entries ${windowText} so far, from the newest ${loaded.length.toLocaleString()} of ${total.toLocaleString()} read. Scroll or load more to reach further back.`
            : `${accessLogs.length.toLocaleString()} entries${windowText ? ` ${windowText}` : ''}.`;

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <PageHeader
                    className="mb-6"
                    title="Access Logs"
                    icon={<ChartColumn />}
                    description="Monitor and audit all access decisions across your resources"
                    timeRange={<TimeRangePicker value={range} onChange={setRange} label="Access log time range" allowAll />}
                    secondaryActions={
                        <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<Filter className="w-4 h-4" />}
                            onClick={() => { setFilters(applied); setFilterModalOpen(true); }} // the modal edits a draft of what is applied
                        >
                            Filters
                        </Button>
                    }
                    onRefresh={() => refetch()}
                    refreshing={isFetching && !isFetchingNextPage}
                />

                {/* Active Filters Badges */}
                {activeFilters.length > 0 && (
                    <div className="mb-6 flex flex-wrap items-center gap-2">
                        <span className="text-sm text-[var(--d2d-text-secondary)]">Active filters:</span>
                        {activeFilters.map((filter) => (
                            <Chip
                                key={filter.key}
                                size="md"
                                onRemove={() => removeFilter(filter.key)}
                                removeLabel={`Remove ${filter.label} filter`}
                            >
                                <span className="font-medium">{filter.label}:</span>{' '}
                                <span>{filter.value}</span>
                            </Chip>
                        ))}
                        {activeFilters.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={resetFilters}>
                                Clear all
                            </Button>
                        )}
                    </div>
                )}

            {/* Loading State */}
            {isLoading && (
                <Card className="p-5">
                    <ListPageSkeleton aria-label="Loading access logs" filterCount={0} rowCount={6} />
                </Card>
            )}

            {/* Error State */}
            {!isLoading && error && (
                <Card>
                    <EmptyState
                        icon={<AlertTriangle />}
                        title="Failed to load access logs"
                        description="Something went wrong while loading access logs."
                        action={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}
                    />
                </Card>
            )}

            {/* Access Logs List */}
            {!isLoading && !error && (
                <>
                    {loadedNote && (
                        <p className="mb-2 text-sm text-[var(--d2d-text-secondary)]" aria-live="polite">
                            {loadedNote}{' '}
                            <span className="text-[var(--d2d-text-muted)]">The time range is applied in the browser: the access log API has no time filter.</span>
                        </p>
                    )}
                    <Card className="overflow-hidden">
                        <LogSearchTable
                            aria-label="Access log entries"
                            rows={accessLogs}
                            columns={columns}
                            rowKey={(log) => log.id}
                            onRowOpen={(log) => setUrl({ entry: log.id })}
                            activeKey={openId || null}
                            hasMore={hasMore}
                            loadingMore={isFetchingNextPage}
                            onLoadMore={() => { if (!isFetchingNextPage) fetchNextPage(); }}
                            empty={
                                loaded.length > 0 ? (
                                    <EmptyState
                                        icon={<SearchX />}
                                        title={`No access logs ${windowText}`}
                                        description="Widen the time range to see older entries."
                                    />
                                ) : activeFilters.length > 0 ? (
                                    <EmptyState
                                        icon={<SearchX />}
                                        title="No access logs match these filters"
                                        description="Try adjusting your filters."
                                    />
                                ) : (
                                    <EmptyState
                                        icon={<FileText />}
                                        title="No access logs yet"
                                        description="Access decisions will appear here as users connect to resources."
                                    />
                                )
                            }
                        />
                    </Card>
                </>
            )}

            {/* Entry detail */}
            <Drawer
                open={!!openId}
                onClose={() => setUrl({ entry: null })}
                size="lg"
                title={openLog ? <span className="flex items-center gap-2">{getResultBadge(openLog.result)} {openLog.action}</span> : 'Access log entry'}
                description={openLog ? <Timestamp value={openLog.created_at} format="both" /> : undefined}
            >
                {openLog ? (
                    <AccessLogDetail log={openLog} userName={getUserDisplayName(openLog.user_id)} resource={resourceOf(openLog)} />
                ) : openLoading || (isLoading && !!openId) ? (
                    <ListPageSkeleton aria-label="Loading access log entry" filterCount={0} rowCount={3} />
                ) : openError ? (
                    <EmptyState icon={<AlertTriangle />} title="Entry not found" description="This access log entry could not be loaded." />
                ) : null}
            </Drawer>

            {/* Filter Modal */}
            <Modal
                open={filterModalOpen}
                onClose={() => setFilterModalOpen(false)}
                size="md"
                dismissOnOverlay={false}
                title={
                    <span className="flex items-center gap-2">
                        <Filter className="w-5 h-5" />
                        Filter Access Logs
                    </span>
                }
                footer={
                    <>
                        <Button variant="secondary" onClick={resetFilters}>
                            Reset
                        </Button>
                        <Button onClick={applyFilters}>
                            Apply Filters
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="User"
                        help={userFilterHelp(Array.isArray(users) ? undefined : users)}
                        value={filters.user_id}
                        onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                    >
                        <option value="">All Users</option>
                        {usersList.map((user: IAMUser) => (
                            <option key={user.id} value={user.id}>{user.name || user.email || user.id}</option>
                        ))}
                    </Select>

                    {/* A picker over GET /resources, not a UUID box (F22). An id
                        already in the URL that the list doesn't hold stays selectable. */}
                    <Select
                        label="Resource"
                        value={filters.resource_id}
                        onChange={(e) => setFilters({ ...filters, resource_id: e.target.value })}
                    >
                        <option value="">All Resources</option>
                        {filters.resource_id && !resourceMap.has(filters.resource_id) && (
                            <option value={filters.resource_id}>{filters.resource_id}</option>
                        )}
                        {resourcesList.map((res) => (
                            <option key={res.id} value={res.id}>{res.external_id} ({res.type})</option>
                        ))}
                    </Select>

                    <Select
                        label="Result"
                        value={filters.result}
                        onChange={(e) => setFilters({ ...filters, result: e.target.value })}
                    >
                        <option value="">All Results</option>
                        <option value="allow">Allow</option>
                        <option value="deny">Deny</option>
                    </Select>
                </div>
            </Modal>
        </div>
    );
};

function AccessLogDetail({ log, userName, resource }: { log: AccessLog; userName: string | null; resource: { name: string; href?: string } }) {
    const field = (label: string, value: React.ReactNode) => (
        <div className="min-w-0">
            <dt className="text-xs font-medium text-[var(--d2d-text-secondary)]">{label}</dt>
            <dd className="mt-0.5 text-sm text-[var(--d2d-text-primary)] min-w-0">{value}</dd>
        </div>
    );
    const link = 'text-[var(--d2d-text-primary)] underline underline-offset-2';
    const policyId = policyIdOf(log);
    const policyLabel = log.policy_version ? `Policy v${log.policy_version}` : 'Policy';
    return (
        <div className="space-y-5">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field('Time', <Timestamp value={log.created_at} />)}
                {field('User', log.user_id ? <Link to={`/users/${encodeURIComponent(log.user_id)}`} className={link}>{userName || log.user_id}</Link> : '—')}
                {field('Log ID', <MonoCell value={log.id} />)}
                {field('User ID', <MonoCell value={log.user_id} />)}
                {resource.name && field('Resource', resource.href ? <Link to={resource.href} className={link}>{resource.name}</Link> : resource.name)}
                {log.resource_id && field('Resource ID', <MonoCell value={log.resource_id} />)}
                {log.request_context?.protocol && field('Protocol', log.request_context.protocol)}
                {(log.policy_version || policyId) && field(
                    'Policy',
                    policyId
                        ? <Link to={policyHref(policyId)} className={link}>{policyLabel}</Link>
                        : <span title="This entry doesn't name the policy that decided it">{policyLabel}</span>,
                )}
            </dl>
            {/* Payloads are drawn through redactJson (decision D4 = A): known
                secret keys and patterns are masked before they reach the screen. */}
            <div>
                <h3 className="text-sm font-medium text-[var(--d2d-text-secondary)] mb-2">Request Context</h3>
                <pre data-testid="access-log-request-context" className="bg-[var(--ds-color-bg-subtle)] rounded-md p-3 text-xs font-mono overflow-x-auto max-h-72 overflow-y-auto text-[var(--d2d-text-primary)]">
                    {redactJson(log.request_context ?? {})}
                </pre>
            </div>
            <div>
                <h3 className="text-sm font-medium text-[var(--d2d-text-secondary)] mb-2">Response Metadata</h3>
                <pre data-testid="access-log-response-metadata" className="bg-[var(--ds-color-bg-subtle)] rounded-md p-3 text-xs font-mono overflow-x-auto max-h-72 overflow-y-auto text-[var(--d2d-text-primary)]">
                    {redactJson(log.response_metadata ?? {})}
                </pre>
            </div>
            <p className="text-xs text-[var(--d2d-text-muted)]">Values that look like secrets are masked.</p>
        </div>
    );
}

export default AccessLogs;
